import React from "react";
import {
  Form,
  Input,
  Select,
  Upload,
  Button,
  Card,
  Row,
  Col,
  message,
  Table,
  Popconfirm,
  Modal,
  Image,
  Space,
} from "antd";
import { UploadOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

const ChecklistForm = ({
  title,
  points,
  onSectionSubmit,
  onSectionDelete,
  submittedData = [],
}) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = React.useState(null);
  const [viewingIndex, setViewingIndex] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 5;

  const onFinish = (values) => {
    onSectionSubmit(values, editingId);
    message.success(editingId !== null ? "Section updated successfully!" : "Section submitted successfully!");
    setEditingId(null);
    form.resetFields();
    setCurrentPage(1);
  };

  const handleEdit = (record) => {
    const fileList = (record.photos || []).map((photo, idx) => ({
      uid: photo.uid || `-${idx}-${Date.now()}`,
      name: photo.name || `photo-${idx + 1}`,
      status: "done",
      url: photo.url,
    }));

    form.setFieldsValue({
      description: record.description || "",
      notes: record.notes || "",
      brand: record.brand || "",
      model: record.model || "",
      rating: record.rating || undefined,
      photos: fileList,
    });

    setEditingId(record.id);
  };

  const handleDelete = (record) => {
    onSectionDelete(record.id);
  };

  const handleView = (record) => {
    setViewingIndex(submittedData.findIndex(r => r.id === record.id));
  };


  const ratingLabels = {
    excellent: "Excellent",
    good: "Good",
    average: "Average",
    fair: "Fair",
    poor: "Poor",
    bad: "Bad",
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Brand / Model",
      key: "brandModel",
      render: (_, record) => `${record.brand || ""} ${record.model || ""}`.trim() || "-",
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      width: 120,
      render: (rating) => ratingLabels[rating] || rating || "-",
    },
    {
      title: "Photos",
      key: "photosCount",
      width: 100,
      render: (_, record) => (record.photos?.length ? `${record.photos.length} photo${record.photos.length > 1 ? "s" : ""}` : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Button type="text" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete this entry?"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    }
  ];

  const viewEntry = viewingIndex !== null ? submittedData[viewingIndex] : null;

  return (
    <Card>
      <h2 style={{ marginBottom: 24 }}>{title}</h2>

      <Card
        size="small"
        style={{ marginBottom: 24, background: "#fafafa" }}
        title="Points to Review"
      >
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          {points.map((point, idx) => (
            <li key={idx} style={{ marginBottom: 8 }}>
              {point}
            </li>
          ))}
        </ol>
      </Card>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: "Please enter description" }]}
        >
          <TextArea rows={4} placeholder="Summarize observations..." />
        </Form.Item>

        <Form.Item label="Additional Notes" name="notes">
          <TextArea rows={3} placeholder="Extra comments, concerns..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Brand"
              name="brand"
              rules={[{ required: true, message: "Brand is required" }]}
            >
              <Input placeholder="Enter brand name..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Model"
              name="model"
              rules={[{ required: true, message: "Model is required" }]}
            >
              <Input placeholder="Enter model..." />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Rating"
          name="rating"
          rules={[{ required: true, message: "Please select rating" }]}
        >
          <Select placeholder="Select overall rating" allowClear>
            <Option value="excellent">Excellent</Option>
            <Option value="good">Good</Option>
            <Option value="average">Average</Option>
            <Option value="fair">Fair</Option>
            <Option value="poor">Poor</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Photos"
          name="photos"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        >
          <Upload
            beforeUpload={() => false} // Prevent auto upload — we handle in parent
            listType="picture"
            multiple
            maxCount={10}
          >
            <Button icon={<UploadOutlined />}>Upload Photos (max 10)</Button>
          </Upload>
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" size="large">
            {editingId !== null ? "Update Entry" : "Save New Entry"}
          </Button>
          {editingId !== null && (
            <Button
              size="large"
              onClick={() => {
                setEditingId(null);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          )}
        </Space>
      </Form>

      {submittedData.length > 0 && (
        <Table
          columns={columns}
          dataSource={submittedData}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: submittedData.length,
            onChange: setCurrentPage,
            showSizeChanger: false,
            size: "small",
          }}
          scroll={{ y: 320 }}
          style={{ marginTop: 32 }}
        />
      )}

      <Modal
        title="Entry Details"
        visible={viewingIndex !== null}
        onCancel={() => setViewingIndex(null)}
        footer={null}
        width={800}
      >
        {viewEntry && (
          <div style={{ lineHeight: 1.8 }}>
            <p><strong>Description:</strong><br />{viewEntry.description || '-'}</p>
            <p><strong>Notes:</strong><br />{viewEntry.notes || 'None'}</p>
            <p><strong>Brand:</strong> {viewEntry.brand || '-'}</p>
            <p><strong>Model:</strong> {viewEntry.model || '-'}</p>
            <p><strong>Rating:</strong> {ratingLabels[viewEntry.rating] || viewEntry.rating || '-'}</p>

            <div style={{ marginTop: 24 }}>
              <strong>Photos:</strong>
              {viewEntry.photos?.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <Image.PreviewGroup>
                    {viewEntry.photos.map((file, idx) => (
                      <Image
                        key={file.uid || idx}
                        width={180}
                        src={file.url}
                        alt={`Photo ${idx + 1}`}
                        style={{ margin: '0 12px 12px 0', borderRadius: 4 }}
                      />
                    ))}
                  </Image.PreviewGroup>
                </div>
              ) : (
                <p style={{ color: '#888', marginTop: 8 }}>No photos attached</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );



};

export default ChecklistForm;